import type { AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import { msalInstance } from './msalInstance';

/**
 * Iframe-aware MSAL helpers. See design-system/IFRAMED-AUTH-PATTERN.md.
 *
 * Microsoft's sign-in pages set `X-Frame-Options: DENY`, so MSAL's
 * `loginRedirect`/`acquireTokenRedirect` get blocked inside an iframe.
 * Fix: detect iframed context and use the *popup* variants — a popup is
 * a top-level window from Microsoft's perspective.
 *
 * Caveat: popups MUST be opened from a user gesture. loginPopup from the
 * AuthGate button is fine; acquireTokenPopup from a silent-renewal
 * failure may be blocked if there's no recent gesture — the error then
 * surfaces, the user clicks Sign in, and the next attempt works.
 */

// Computed once at module load. window.top !== window.self throws on some
// cross-origin iframe configs; treat the throw as "definitely iframed".
export const inIframe: boolean = (() => {
  try {
    return window.top !== window.self;
  } catch {
    return true;
  }
})();

export async function signIn(scopes: string[]): Promise<AuthenticationResult | void> {
  if (inIframe) {
    return msalInstance.loginPopup({ scopes });
  }
  return msalInstance.loginRedirect({ scopes });
}

export async function reAcquireToken(
  scopes: string[],
  account: AccountInfo
): Promise<AuthenticationResult | void> {
  if (inIframe) {
    return msalInstance.acquireTokenPopup({ scopes, account });
  }
  return msalInstance.acquireTokenRedirect({ scopes, account });
}
